import { Component, ViewChild, ElementRef, Renderer2, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { InteractivityMediaService } from '../../services/interactivity-media.service';
import { WidgetType } from '../../enums/WidgetType';
import { InteractivityWidgetService } from '../../services/interactivity-widget.service';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { BbDragEvent } from 'app/directives/bb-drag.directive';

@Component({
	selector: 'timeline',
	templateUrl: './timeline.component.html',
	styleUrls: ['./timeline.component.scss']
})

export class TimelineComponent implements OnInit, OnDestroy {
	@ViewChild('timeIndicator') timeIndicator: ElementRef;
	@ViewChild('timeRuler') timeRuler: ElementRef;
	showButtonBar = false;
	public showGhost = false;
	public widgetType: WidgetType;
	public newWidgetConfig: any = {};
	public nonVisualWidgets: any[];

	endscreenEnabled = false;
	extraTimeInfo: any = {};
	extraAreaStyle: any = {};

	private duration: number;
	private durationChangedSubscription: Subscription;
	private timeChangedSubscription: Subscription;
	private dragging: boolean;
	private mediaCurrentTime = 0;

	private subscriptions: Subscription = new Subscription();
	private zoomFactor: number;
	private styleBlock: HTMLElement;

	constructor(
		private interactivityMediaService: InteractivityMediaService,
		private renderer: Renderer2,
		private interactivityWidgetService: InteractivityWidgetService,
		public interactivityEditorService: InteractivityEditorService
	) {
		this.dragging = false;
		this.nonVisualWidgets = [
			{
				label: WidgetType.Audio,
				value: WidgetType.Audio
			},
			{
				label: WidgetType.Timer,
				value: WidgetType.Timer
			}
		];
		// chapters will be added when the parentEntity is set and doesn't have chapters
	}

	@HostListener('window:resize')
	onWindowResize() {
		this.updateTimeIndicator();
	}

	ngOnInit() {
		this.durationChangedSubscription = this.interactivityEditorService.durationSubject.subscribe(
			(duration: number) => {
				this.duration = duration;
			});

		this.timeChangedSubscription = this.interactivityMediaService.currentTimeSubject.subscribe(
			(time: number) => {
				this.mediaCurrentTime = time;
				if (!this.dragging) {
					this.updateTimeIndicator();
				}
			});

		this.subscriptions.add(this.interactivityEditorService.parentEntitySubject.subscribe((parentEntity) => {
			if (!(parentEntity as any)?.chapters?.length) {
				if (!(this.nonVisualWidgets.find((option) => option.value === WidgetType.Chapter))) {
					this.nonVisualWidgets.push({
						label: WidgetType.Chapter,
						value: WidgetType.Chapter
					});
				}
			} else {
				this.nonVisualWidgets = this.nonVisualWidgets.filter((widget) => widget.value !== WidgetType.Chapter);
			}
		}));

		this.subscriptions.add(this.interactivityEditorService.timelineOptionsSubject.subscribe((newOptions) => {
			this.endscreenEnabled = newOptions && newOptions.exitArea === true;
		}));

		this.subscriptions.add(this.interactivityEditorService.extraTimeMultiplierSubject.subscribe((extraTimeMultiplier: any) => {
			this.extraAreaStyle = {
				width: (1 - (1 / extraTimeMultiplier)) * 100 + '%'
			};
		}));

		this.styleBlock = document.createElement('style');
		document.head.appendChild(this.styleBlock);
		this.subscriptions.add(this.interactivityEditorService.trackZoomSubject.subscribe((trackZoom: any) => {
			if (trackZoom && trackZoom.zoomFactor && trackZoom.zoomFactor !== this.zoomFactor) {
				this.zoomFactor = trackZoom.zoomFactor;
			}
			// bit hacky, but probably better then updating all tracks in the DOM separately
			this.styleBlock.innerHTML = `
			.bb-track-zoom {
				position: relative;
				overflow: hidden;
				border-right: 10px solid transparent;
				width: ${(1 / trackZoom.zoomFactor) * 100}% !important;
				margin-left: -${((trackZoom.offset * (1 / trackZoom.zoomFactor)) * 100)}% !important;
			}
			.timeline-indicator-top .bb-track-zoom {
				border-right: 10px solid transparent;
			}`;

			this.updateTimeIndicator();
		}));
	}


	updateTimeIndicator() {
		if (!this.timeIndicator) {
			return;
		}
		const time = this.mediaCurrentTime;
		let timeIndicatorPosition = '';
		if (time > this.duration) {
			timeIndicatorPosition = this.timeIndicator.nativeElement.parentElement.offsetWidth + 'px';
		} else {
			timeIndicatorPosition = ((time * (this.timeIndicator.nativeElement.parentElement.offsetWidth)) / this.duration) + 'px';
		}
		this.renderer.setStyle(this.timeIndicator.nativeElement, 'left', timeIndicatorPosition);
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		if (this.durationChangedSubscription) {
			this.durationChangedSubscription.unsubscribe();
		}
		if (this.timeChangedSubscription) {
			this.timeChangedSubscription.unsubscribe();
		}
	}

	onTimeRulerClick(ev: MouseEvent) {
		if (!this.timeIndicator) {
			return;
		}
		const time: number = this.duration * (ev.offsetX / (this.timeIndicator.nativeElement.parentElement.offsetWidth));
		this.interactivityMediaService.seekToTime(time);
	}

	onTimeIndicatorDragging(ev: BbDragEvent) {
		if (!this.timeIndicator) {
			return;
		}
		this.dragging = true;

		const time: number = this.duration * (ev.left / (this.timeIndicator.nativeElement.parentElement.offsetWidth));
		this.interactivityMediaService.seekToTime(time);
	}

	public onTimeIndicatorDragEnd() {
		this.dragging = false;
	}

	onAddTrackButtonBarOpened() {
		this.showButtonBar = true;
		this.showGhost = true;
	}

	closeButtonBar() {
		this.showButtonBar = false;
		this.showGhost = false;
	}

	itemSelected(selectedWidgetItem: any): void {
		if (selectedWidgetItem.value) {
			this.interactivityWidgetService.createWidget(selectedWidgetItem.value, null,
				this.newWidgetConfig.targetIndex, this.newWidgetConfig.groupId);
		}
		this.closeButtonBar();
	}

	mouseOverItem(widgetItem: any): void {
		this.widgetType = widgetItem.value;
	}

	endscreenChange(useEndscreen: boolean) {
		this.interactivityEditorService.setUseEndscreen(useEndscreen);
	}

}
